/**
 * The signed-in tenant's real identity and payment rows (COL-533).
 *
 * Both queries read exactly what V1 reads — same tables, same filters — so the
 * two dashboards share one source of truth. They do NOT share V1's cache
 * entries: each key below carries a `tenant-v2` segment, because V1's
 * equivalents cache a different SHAPE (a truncated history, and proof rows
 * enriched with a field V1's UI needs). Each key keeps V1's prefix, so V1's
 * existing invalidations still reach these. See the note on each query.
 */

import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

/**
 * The tenant's name as the system of record holds it.
 *
 * `profiles.name`, not `user_metadata` — auth metadata is whatever was typed
 * at signup and is never updated when an admin corrects a tenant's name, so
 * greeting from it can address someone by a name no other screen shows.
 */
export function useTenantProfileName() {
  const { user } = useAuth();

  return useQuery<string | null>({
    queryKey: ["tenant-v2", "profile-name", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return (data as unknown as { name: string | null })?.name ?? null;
    },
  });
}

/**
 * Every payment row for this tenant.
 *
 * Deliberately NOT date-bounded and NOT limited, matching V1's upcoming
 * query: `selectEarliestUnpaidRentPayment` has to see every candidate to pick
 * correctly, and an overdue pro-rated first month must still surface as "next
 * payment" rather than being skipped for a later full-month row. Only
 * `canceled` is excluded — a `failed` row is a bounced collection and is still
 * owed, and a raw `status: 'paid'` row may not be effectively paid yet, so
 * both stay in for the canonical predicate downstream to judge.
 *
 * Keyed under V1's `payments` prefix but NOT on V1's entry — see the note on
 * the key below.
 */
export function useTenantPayments() {
  const { user } = useAuth();

  return useQuery<any[]>({
    // Distinct from V1's ["payments", userId], which caches a DESCENDING,
    // newest-24 history that still includes canceled rows. Sharing the key
    // would hand that truncated list to the deposit and next-payment
    // resolvers on a dashboard switch, and an older unpaid row would vanish.
    // The suffix goes AFTER the user id, not before it: React Query matches
    // key prefixes positionally, so V1's invalidateQueries(["payments", uid])
    // only reaches this if the first two elements line up. With the segment in
    // the middle it matched nothing and this ledger went stale on every V1
    // mutation — the comment here claimed otherwise. Same shape as the booking
    // and proof keys.
    queryKey: ["payments", user?.id, "tenant-v2-ledger"],
    enabled: !!user?.id,
    // Matches V1's 30s poll. Window-focus alone never fires on a dashboard
    // left open, so an admin approving a deposit or marking rent paid would
    // not reach a tenant sitting on this page — and in V2 that evidence also
    // drives the journey stage, so whole tabs would stay shut until a reload.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase as any)
        .from("payments" as any)
        .select("*")
        .eq("user_id", user.id)
        .neq("status", "canceled")
        .order("due_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

/**
 * Latest proof per payment id, keyed for the paid predicate.
 *
 * Needed because "paid" is not `status = 'paid'`: Stripe rows are trusted from
 * the DB, but a manual or bank-transfer row only counts once an approved proof
 * exists — deleting the proof has to drop it back out of paid. The canonical
 * rule in `lib/paymentStatus` takes this lookup as its second argument.
 *
 * Throws rather than swallowing a failure, so react-query keeps the last good
 * map instead of resetting to `{}` — an empty map would briefly make every
 * proof-backed payment look unpaid.
 */
export function useTenantPaymentProofs(paymentIds: readonly string[]) {
  const { user } = useAuth();
  const key = [...paymentIds].sort();

  return useQuery<Record<string, any>>({
    // Suffixed, for the same reason as the ledger above. V1 builds its proof
    // map from the identical key shape — ["payment-proofs", uid, sortedIds] —
    // but its queryFn ENRICHES each row with `rejectionReasonToShow`, which
    // its payment UI reads in several places. With the same key and the same
    // id list the two entries are one, so whichever dashboard fetched first
    // wins: a tenant arriving at V1 through the "Manage payments" hand-off
    // would get this raw map and see no rejection reason on a rejected proof.
    queryKey: ["payment-proofs", user?.id, "tenant-v2", key],
    enabled: !!user?.id && key.length > 0,
    refetchInterval: 30_000,
    queryFn: async () => {
      if (!user?.id || key.length === 0) return {};
      const { data, error } = await (supabase as any)
        .from("payment_proofs" as any)
        .select("*")
        .in("payment_id", key)
        .order("created_at", { ascending: false });
      if (error) throw error;
      // Ordered newest-first, so the first row seen per payment is the latest.
      const latest: Record<string, any> = {};
      for (const proof of data || []) {
        const id = String((proof as any).payment_id);
        if (!(id in latest)) latest[id] = proof;
      }
      return latest;
    },
  });
}
